import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Video, Plus, Trash2, Loader2, ChevronLeft, X, Link as LinkIcon,
  Eye, EyeOff, Calendar, ExternalLink, Copy, BarChart3,
} from "lucide-react";

/**
 * Producer Screener Room — secure preview URL library maintained by the
 * production house. Sales agents can pick a screener when logging a
 * deal (only for the producer they've linked via film_producer_org_id).
 */
export default function ProducerScreeners() {
  const [screeners, setScreeners] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/producer/screeners/mine")
      .then(({ data }) => setScreeners(data.screeners || []))
      .catch(() => setScreeners([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="producer-screeners-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="producer-screeners-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Producer · Preview Room</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-indigo-300"
              data-testid="producer-screeners-heading">
            Screener Room
          </h1>
          <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed">
            Every preview URL you want linked sales agents to be able
            to share with buyers. Only sales agents you&apos;ve already linked
            to a deal on this platform can browse your active screeners.
          </p>
        </div>
        <Button className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-indigo-600 text-white hover:bg-indigo-500"
                onClick={() => setShowAdd(true)}
                data-testid="producer-screeners-add-btn">
          <Plus size={13} className="mr-1.5"/> Add Screener
        </Button>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="producer-screeners-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading…
          </div>
        ) : screeners.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="producer-screeners-empty">
            <Video size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No screeners yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Add your Vimeo/Google Drive/private-CDN preview link for
              each film. Once linked to a deal, sales agents will see it
              to share with the specific buyer.
            </div>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="producer-screeners-list">
            {screeners.map((s) => (
              <ScreenerRow key={s.id} row={s} refresh={refresh}/>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddScreenerModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ScreenerRow({ row, refresh }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/producer/screeners/${row.id}/toggle`);
      refresh();
    } catch { toast.error("Toggle failed."); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm(`Delete screener for "${row.film_title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/producer/screeners/${row.id}`);
      toast.success("Screener removed.");
      refresh();
    } catch { toast.error("Delete failed."); } finally { setBusy(false); }
  };
  const copyLink = async () => {
    try {
      const { data } = await api.get(`/producer/screeners/${row.id}/share`);
      // The backend origin from api.defaults.baseURL is `${host}/api`.
      const baseOrigin = (api.defaults?.baseURL || window.location.origin).replace(/\/api\/?$/, "");
      const trackerUrl = `${baseOrigin}${data.tracker_path}`;
      await navigator.clipboard.writeText(trackerUrl);
      toast.success("Tracked share link copied.");
    } catch { toast.error("Could not build share link."); }
  };

  const views = Number(row.view_count || 0);
  return (
    <div className="cs-card rounded-xl p-4 flex items-start gap-4 flex-wrap"
         data-testid={`producer-screener-${row.id}`}>
      <div className="w-11 h-11 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center shrink-0">
        <Video size={19}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-base uppercase tracking-tight">{row.film_title}</div>
          {row.film_year && (
            <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.16em] uppercase text-white/60">
              {row.film_year}
            </span>
          )}
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold ${
            row.is_active ? "bg-emerald-500/25 text-emerald-200"
                          : "bg-white/8 text-white/50"
          }`}
                data-testid={`producer-screener-status-${row.id}`}>
            {row.is_active ? "Active" : "Hidden"}
          </span>
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1 ${
            views > 0 ? "bg-teal-500/25 text-teal-200" : "bg-white/6 text-white/45"
          }`}
                data-testid={`producer-screener-views-${row.id}`}>
            <BarChart3 size={9}/> {views} view{views === 1 ? "" : "s"}
          </span>
        </div>
        <a href={row.url} target="_blank" rel="noreferrer"
           className="mt-1 text-[12px] text-indigo-300 hover:text-indigo-200 truncate max-w-full inline-flex items-center gap-1"
           data-testid={`producer-screener-url-${row.id}`}>
          <LinkIcon size={11}/> {row.url}
          <ExternalLink size={10}/>
        </a>
        {row.access_note && (
          <div className="mt-1 text-[11px] text-white/55 italic">{row.access_note}</div>
        )}
        <div className="mt-1 flex flex-wrap gap-4 text-[10px] text-white/45">
          {row.expires_at && (
            <span className="flex items-center gap-1"><Calendar size={9}/> Expires · {row.expires_at}</span>
          )}
          {row.last_viewed_at && (
            <span className="flex items-center gap-1"><Eye size={9}/> Last opened · {row.last_viewed_at.slice(0, 10)}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={copyLink} disabled={busy}
                className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition"
                title="Copy tracked share link"
                data-testid={`producer-screener-share-${row.id}`}>
          <Copy size={13}/>
        </button>
        <button onClick={toggle} disabled={busy}
                className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition"
                title={row.is_active ? "Hide from SAs" : "Un-hide"}
                data-testid={`producer-screener-toggle-${row.id}`}>
          {busy ? <Loader2 size={13} className="animate-spin"/> :
            row.is_active ? <Eye size={13}/> : <EyeOff size={13}/>}
        </button>
        <button onClick={remove} disabled={busy}
                className="p-2 rounded-lg text-white/40 hover:text-red-400 transition"
                data-testid={`producer-screener-delete-${row.id}`}>
          <Trash2 size={13}/>
        </button>
      </div>
    </div>
  );
}

function AddScreenerModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    film_title: "", film_year: "", url: "",
    access_note: "", expires_at: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.film_title.trim().length < 1) return toast.error("Film title required.");
    if (!/^https?:\/\//i.test(form.url.trim())) return toast.error("URL must start with http:// or https://");
    setBusy(true);
    try {
      await api.post("/producer/screeners", {
        ...form,
        film_year: form.film_year ? Number(form.film_year) : null,
        url: form.url.trim(),
        is_active: true,
      });
      toast.success("Screener added.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add screener.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="producer-screeners-add-modal">
      <div className="w-full max-w-xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
              <Video size={18}/>
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-tight">Add Screener</div>
              <div className="text-[11px] text-white/55">One preview URL per film.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16}/></button>
        </div>
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Film Title *">
              <Input value={form.film_title} onChange={set("film_title")}
                     className="bg-black/40 border-white/15"
                     data-testid="producer-screener-title"/>
            </Field>
            <Field label="Year">
              <Input value={form.film_year} onChange={set("film_year")} type="number" min={1900} max={2100}
                     className="bg-black/40 border-white/15"
                     data-testid="producer-screener-year"/>
            </Field>
          </div>
          <Field label="Preview URL *">
            <Input value={form.url} onChange={set("url")}
                   placeholder="https://vimeo.com/… · https://drive.google.com/… · https://<cdn>/watermark/…"
                   className="bg-black/40 border-white/15"
                   data-testid="producer-screener-url"/>
          </Field>
          <Field label="Access Note">
            <Input value={form.access_note} onChange={set("access_note")}
                   placeholder="Password: XYZ · Region-locked · One-time view…"
                   className="bg-black/40 border-white/15"
                   data-testid="producer-screener-access"/>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Expires At">
              <Input value={form.expires_at} onChange={set("expires_at")} type="date"
                     className="bg-black/40 border-white/15"
                     data-testid="producer-screener-expires"/>
            </Field>
            <Field label="Notes (internal)">
              <Input value={form.notes} onChange={set("notes")}
                     className="bg-black/40 border-white/15"
                     data-testid="producer-screener-notes"/>
            </Field>
          </div>
        </div>
        <div className="mt-8 flex gap-3">
          <Button variant="ghost" onClick={onClose}
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5">
            Cancel
          </Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="producer-screener-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : "Save Screener"}
          </Button>
        </div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
